import { Store } from "../utils/Store";
import { SessionStore } from "../uidispatcherapi/SessionStore";
import type { GetCustomerParams } from "../server/CustomerDataRequests";
import * as CustomerDataRequests from "../server/CustomerDataRequests";
import { ErrorHandler } from "./ErrorHandler";
import type { CartContextStoreImpl } from "./common-cart/CartContextStore";
import { CustomerCartContextStore } from "./common-cart/CartContextStore";
import type { CheckoutCustomer, CustomerAddress } from "../model/CheckoutData";
import type { ICustomerDataStore } from "./ICustomerDataStore";
import { CustomerDataActions } from "../actions/CustomerDataActions";
import { intlRoot } from "../IntlRootMixin";

type CustomerDataStoreState = {
	customerData: CheckoutCustomer | null | undefined;
	cardholderNumber: string | null | undefined;
	getCustomerParams: GetCustomerParams | null | undefined;
};
export class CustomerDataStoreImpl extends Store implements ICustomerDataStore {
	_customerCache: Map<string, CheckoutCustomer | null | undefined>;
	_errorHandler = new ErrorHandler([
		{
			canHandle: ({ code }) => code === "OC-217",
			handle: ({ code, value }, { send }) => {
				send({
					type: "user-error",
					text: intlRoot.formatMessage({ id: "ORC_ERROR_LIMIT_STOCK_QUANTITY" }, { customerId: value }),
					id: code,
				});
			},
		},
	]);
	_cartContextStore: CartContextStoreImpl;
	_state!: CustomerDataStoreState;

	constructor(cartContextStore: CartContextStoreImpl) {
		super();
		this._customerCache = new Map();
		this._cartContextStore = cartContextStore;
		this.listenTo(SessionStore, () => {
			this.handleCartContextChange().catch((error) => {
				console.error("handleCartContextChange in customer data store failed with ", error);
			});
		});
		this.listenTo(this._cartContextStore, (...args) => {
			this.handleCartContextChange(...args).catch((error) => {
				console.error("handleCartContextChange in customer data store failed with ", error);
			});
		});
		this.listenTo(CustomerDataActions.reloadCustomerData, () => {
			this.forceUpdate().catch((error) => {
				console.error("forceUpdate in customer data store failed with ", error);
			});
		});
		this.resetState();
	}

	resetState() {
		this._state = {
			customerData: null,
			cardholderNumber: null,
			getCustomerParams: null,
		};
		this.trigger();
	}

	forceUpdate() {
		return this.update(true);
	}

	handleCartContextChange() {
		return this.update(false);
	}

	async update(forceUpdate: boolean) {
		const loggedIn = SessionStore.loggedIn();
		const { customerId, country, mode } = this._cartContextStore.getState();

		const cardholderNumber =
			mode === "SESSION"
				? SessionStore.getCardholderNumber()
				: this._cartContextStore.getState().cardholderNumber;

		if (!loggedIn || !country || !customerId || !cardholderNumber) {
			if (this._state.customerData) this.resetState();
			return;
		}

		const params: GetCustomerParams = {
			country,
			customerId,
			cardholderId: cardholderNumber,
		};
		const hasNoNeedToUpdate = this.stateHasSameParams(params);

		if (!forceUpdate && hasNoNeedToUpdate) {
			return;
		}

		try {
			this._state = { ...this._state, getCustomerParams: params };
			const customerData = await CustomerDataRequests.getCustomer(params);

			//avoid updating the state and triggering if the request is outdated
			if (this.stateHasSameParams(params)) {
				this._state = { ...this._state, cardholderNumber, customerData };
				this.trigger();
			}
		} catch (err) {
			this._errorHandler.onError(null, { ...(err as any), source: "CustomerDataStore.handleCartContextChange" });

			this.resetState();
		}
	}

	stateHasSameParams(params: GetCustomerParams): boolean {
		const { getCustomerParams } = this._state;
		return (
			!!getCustomerParams &&
			params.customerId === getCustomerParams.customerId &&
			params.cardholderId === getCustomerParams.cardholderId
		);
	}

	getCustomer(): CheckoutCustomer | null | undefined {
		return this._state.customerData;
	}

	getState() {
		return { ...this._state };
	}

	static filterDeliveryAddressByStoreId(address: CustomerAddress, storeId?: string) {
		return (
			address.accessibleByCurrentUser &&
			address.addressType === "DELIVERY" &&
			(!storeId || address.deliveryStore === storeId)
		);
	}

	getDeliveryAddresses(customerId: string, storeId?: string): CustomerAddress[] {
		return this._state.customerData && this._state.customerData.customerId === customerId
			? (Object.values(this._state.customerData.addresses).filter(
					(address) => address && CustomerDataStoreImpl.filterDeliveryAddressByStoreId(address, storeId),
				) as CustomerAddress[])
			: ([] as CustomerAddress[]);
	}

	static getDeliveryAddressesFromCustomerData(customerData: CheckoutCustomer, storeId?: string): CustomerAddress[] {
		return Object.values(customerData.addresses).filter(
			(address) => address && CustomerDataStoreImpl.filterDeliveryAddressByStoreId(address, storeId),
		) as CustomerAddress[];
	}

	getInvoiceAddress(customerId: string | null | undefined): CustomerAddress | null | undefined {
		return (
			(this._state.customerData &&
				customerId === this._state.customerData.customerId &&
				Object.values(this._state.customerData.addresses).filter(
					(address) => address && address.accessibleByCurrentUser && address.addressType === "INVOICE",
				)[0]) ||
			null
		);
	}

	async getSpecificCustomer(
		customerId: string,
		cardholderId: string,
		throwOnFailure = false,
	): Promise<CheckoutCustomer | null | undefined> {
		if (this._customerCache.get(customerId)) return this._customerCache.get(customerId);

		try {
			const params: GetCustomerParams = {
				country: this._cartContextStore.getState().country || "",
				customerId,
				cardholderId,
			};
			const customer = await CustomerDataRequests.getCustomer(params);

			this._customerCache.set(customerId, customer);

			return customer;
		} catch (e) {
			if (throwOnFailure) {
				throw e;
			}

			this._errorHandler.onError(null, e as any);
		}

		return null;
	}
}
export const CustomerDataStore = new CustomerDataStoreImpl(CustomerCartContextStore);
